import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQC } from './QCContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Download,
  FileText,
  Filter,
  Users,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  Search,
} from 'lucide-react';

export const QCReports = () => {
  const navigate = useNavigate();
  const { api, user } = useQC();
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
    employee_class: '',
    status: 'all',
  });

  // Overtime report data
  const [overtimeData, setOvertimeData] = useState([]);
  const [loadingOT, setLoadingOT] = useState(false);

  const fetchOvertimeReport = async () => {
    setLoadingOT(true);
    try {
      const response = await api.get('/canvass/bid-order');
      // Handle both array and object response formats
      const data = response.data;
      if (Array.isArray(data)) {
        setOvertimeData(data);
      } else if (data && Array.isArray(data.users)) {
        setOvertimeData(data.users);
      } else {
        setOvertimeData([]);
      }
    } catch (error) {
      toast.error('Failed to load overtime report');
    } finally {
      setLoadingOT(false);
    }
  };

  const generateCanvassReport = async () => {
    setLoading(true);
    try {
      // Fetch forced fill runs and acknowledgements
      const [acksRes, escalationsRes] = await Promise.all([
        api.get('/admin/canvass/pending-acknowledgements').catch(() => ({ data: [] })),
        api.get('/admin/canvass/escalations').catch(() => ({ data: [] })),
      ]);
      
      setReport({
        pending_acknowledgements: acksRes.data || [],
        escalations: escalationsRes.data || [],
        generated_at: new Date().toISOString(),
      });
      toast.success('Report generated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = (data, filename) => {
    if (!data || data.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(h => `"${row[h] || ''}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('CSV downloaded!');
  };

  useEffect(() => {
    fetchOvertimeReport();
  }, []);

  return (
    <div className="space-y-6" data-testid="qc-reports">
      <Button variant="ghost" onClick={() => navigate('/quickcanvass')} className="gap-2">
        <ArrowLeft className="h-4 w-4" />
        Back to Dashboard
      </Button>

      <div>
        <h1 className="text-4xl md:text-5xl font-black tracking-tight">
          Reports
        </h1>
        <p className="text-muted-foreground mt-2">
          Generate and export canvassing reports
        </p>
      </div>

      <Tabs defaultValue="overtime" className="w-full">
        <TabsList>
          <TabsTrigger value="overtime" className="gap-2">
            <TrendingUp className="h-4 w-4" />
            Overtime Report
          </TabsTrigger>
          <TabsTrigger value="canvassing" className="gap-2">
            <Users className="h-4 w-4" />
            Canvassing Report
          </TabsTrigger>
          <TabsTrigger value="acknowledgements" className="gap-2">
            <CheckCircle className="h-4 w-4" />
            Acknowledgements
          </TabsTrigger>
        </TabsList>

        {/* Overtime Report */}
        <TabsContent value="overtime" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Overtime Rankings</CardTitle>
                  <CardDescription>Current overtime hours by employee</CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => downloadCSV(overtimeData.map(d => ({
                    name: d.name,
                    email: d.email,
                    employee_class: d.employee_class,
                    overtime_hours: d.overtime_hours,
                    rank: d.rank,
                  })), 'overtime_report')}
                  className="gap-2"
                >
                  <Download className="h-4 w-4" />
                  Export CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingOT ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Rank</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Class</TableHead>
                      <TableHead>Overtime Hours</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {overtimeData.map((emp, idx) => (
                      <TableRow key={emp.user_id}>
                        <TableCell className="font-bold">#{idx + 1}</TableCell>
                        <TableCell>{emp.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{emp.employee_class || 'N/A'}</Badge>
                        </TableCell>
                        <TableCell>{emp.overtime_hours?.toFixed(1) || '0.0'}h</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Canvassing Report */}
        <TabsContent value="canvassing" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Canvassing Activity</CardTitle>
                  <CardDescription>Generate report on canvassing activity</CardDescription>
                </div>
                <Button onClick={generateCanvassReport} disabled={loading} className="gap-2">
                  {loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <FileText className="h-4 w-4" />
                  )}
                  Generate Report
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {report ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2">
                          <Clock className="h-5 w-5 text-orange-500" />
                          <span className="text-2xl font-bold">{report.pending_acknowledgements?.length || 0}</span>
                        </div>
                        <p className="text-sm text-muted-foreground">Pending Acknowledgements</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2">
                          <XCircle className="h-5 w-5 text-red-500" />
                          <span className="text-2xl font-bold">{report.escalations?.length || 0}</span>
                        </div>
                        <p className="text-sm text-muted-foreground">Escalations</p>
                      </CardContent>
                    </Card>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Generated at: {new Date(report.generated_at).toLocaleString()}
                  </p>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  Click "Generate Report" to view canvassing statistics
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Acknowledgements Report */}
        <TabsContent value="acknowledgements" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Acknowledgement Status</CardTitle>
              <CardDescription>Track employee responses to assignments</CardDescription>
            </CardHeader>
            <CardContent>
              {report?.pending_acknowledgements?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Job</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Time Remaining</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.pending_acknowledgements.map((ack) => (
                      <TableRow key={ack.id}>
                        <TableCell>{ack.user_name}</TableCell>
                        <TableCell>{ack.job_title}</TableCell>
                        <TableCell>{ack.crew_role_name}</TableCell>
                        <TableCell>
                          <Badge variant="secondary">Pending</Badge>
                        </TableCell>
                        <TableCell>
                          {ack.time_remaining ? `${Math.floor(ack.time_remaining / 60)}m` : 'N/A'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No pending acknowledgements. Generate a report first.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default QCReports;
